/**
 * Sysadmin Catalog → Tool detail (PR-H).
 *
 * 4 tabs: Overview · Versions · Assignments · Audit.
 * Lifecycle action bar across the top: Publish / Unpublish / Edit / Delete.
 */

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import {
  ArrowLeft,
  Wrench,
  CheckCircle2,
  Edit,
  Trash2,
  PauseCircle,
  Archive,
  Plus,
  X,
  AlertTriangle,
  Users,
  History,
  ScrollText,
  FileJson,
  ShieldCheck,
  Search,
  Save,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
// R5 P0-1/P0-2 fix: list-page helpers moved to catalog.tools.index.tsx
// when the parent route became a pure <Outlet/> layout (2026-05-13).
import {
  StatusBadge,
  SourceKindBadge,
} from './catalog.tools.index';
import {
  useSysadminTool,
  useSysadminToolVersions,
  useSysadminToolAssignments,
  usePublishSysadminTool,
  useUnpublishSysadminTool,
  useUpdateSysadminTool,
  useDeleteSysadminTool,
  useAssignSysadminTool,
  useRevokeSysadminToolAssignment,
  useTenantsForAssignment,
  type ToolPolicy,
  type ToolSourceType,
  type UnpublishNextStatus,
  type SysadminToolDetail,
} from '@/lib/use-sysadmin-tools';
import { useAuditStream } from '@/lib/use-sysadmin-audit';
// PR-C-MINIMAL — JSON parsing helpers shared across the 5 tool
// authoring surfaces. Keeps the EditDrawer's handleSave in lockstep
// with the sibling sysadmin/catalog.tools.tsx create flow.
import {
  parseJsonTyped,
  tryParseJsonArrayOfStrings,
} from '@/components/tools-authoring-shared/tools-shared.helpers';

export const Route = createFileRoute('/sysadmin/catalog/tools/$id')({
  component: SysadminToolDetailPage,
});

function SysadminToolDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();

  const { data, isLoading, error, refetch } = useSysadminTool(id);
  const tool = data?.data;

  const publish = usePublishSysadminTool();
  const unpublish = useUnpublishSysadminTool();
  const del = useDeleteSysadminTool();

  const [unpublishOpen, setUnpublishOpen] = useState(false);
  const [unpublishReason, setUnpublishReason] = useState('');
  const [unpublishNext, setUnpublishNext] = useState<UnpublishNextStatus>(
    'deprecated',
  );
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const handlePublish = async () => {
    if (!tool) return;
    try {
      await publish.mutateAsync({ id: tool.id });
      toast.success(`Published "${tool.slug}"`);
    } catch (err) {
      toast.error('Publish failed', {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  const handleUnpublish = async () => {
    if (!tool) return;
    if (unpublishReason.trim().length < 10) {
      toast.error('Reason must be at least 10 characters');
      return;
    }
    try {
      const r = await unpublish.mutateAsync({
        id: tool.id,
        reason: unpublishReason.trim(),
        nextStatus: unpublishNext,
      });
      toast.success(
        `Tool moved to ${unpublishNext}` +
          (r.revokedAssignments > 0
            ? ` — revoked ${r.revokedAssignments} assignments`
            : ''),
      );
      setUnpublishOpen(false);
      setUnpublishReason('');
    } catch (err) {
      toast.error('Unpublish failed', {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  const handleDelete = async () => {
    if (!tool) return;
    try {
      await del.mutateAsync({ id: tool.id });
      toast.success(`Deleted "${tool.slug}"`);
      navigate({ to: '/sysadmin/catalog/tools' });
    } catch (err) {
      toast.error('Delete failed', {
        description: err instanceof Error ? err.message : undefined,
      });
      setConfirmDelete(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse text-muted-foreground">Loading tool…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6">
        <Card className="border-destructive/30">
          <CardContent className="py-6 text-sm text-destructive flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>Failed to load: {error.message}</div>
          </CardContent>
        </Card>
      </div>
    );
  }
  if (!tool) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="py-6 text-sm">Tool not found.</CardContent>
        </Card>
      </div>
    );
  }

  const canPublish = tool.status === 'draft' || tool.status === 'deprecated';
  const canUnpublish =
    tool.status === 'published' || tool.status === 'deprecated';
  const canDelete =
    tool.status === 'draft' || tool.status === 'retired';

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-4">
      <div className="flex items-center gap-2">
        <Link
          to="/sysadmin/catalog/tools"
          title="Back to tools list"
          className="inline-flex items-center justify-center h-9 w-9 rounded-md hover:bg-accent hover:text-accent-foreground transition-colors"
          aria-label="Back to tools list"
        >
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <Wrench className="h-6 w-6 text-muted-foreground" />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold tracking-tight font-mono">
              {tool.slug}
            </h1>
            <StatusBadge status={tool.status} />
            <SourceKindBadge sourceType={tool.sourceType} />
            <Badge variant="outline" className="text-[10px]">
              v{tool.version}
            </Badge>
          </div>
          <div className="text-sm text-muted-foreground mt-0.5">
            {tool.displayName}
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => refetch()}
          title="Refresh"
        >
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {canPublish && (
          <Button
            size="sm"
            className="gap-1.5"
            onClick={handlePublish}
            disabled={publish.isPending}
            data-testid="publish-button"
            title="Move tool to status=published. Tenants can be assigned."
          >
            <CheckCircle2 className="h-3.5 w-3.5" />
            {publish.isPending ? 'Publishing…' : 'Publish'}
          </Button>
        )}
        {/*
          Issue #746 — PR-U1 collapsed catalog lifecycle from a 4-state
          DAG to a 2-state model (draft|published) with `enabled` for
          soft-disable and `deleted_at` for soft-delete. The backend
          POST /catalog-tools/:id/unpublish now returns 410 Gone, so
          this Unpublish button was a dead control. Soft-disable is
          available via the Enabled toggle on the details tab;
          soft-delete via the existing Delete button below.
        */}
        <Button
          size="sm"
          variant="outline"
          className="gap-1.5"
          onClick={() => setEditOpen(true)}
          title="Edit metadata. Creates a new version."
        >
          <Edit className="h-3.5 w-3.5" />
          Edit
        </Button>
        {canDelete && (
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5 text-destructive hover:bg-destructive/10"
            onClick={() => setConfirmDelete(true)}
            title="Permanently delete. Only allowed in draft or retired status."
          >
            <Trash2 className="h-3.5 w-3.5" />
            Delete
          </Button>
        )}
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview" className="gap-1.5">
            <FileJson className="h-3.5 w-3.5" /> Overview
          </TabsTrigger>
          <TabsTrigger value="versions" className="gap-1.5">
            <History className="h-3.5 w-3.5" /> Versions
          </TabsTrigger>
          <TabsTrigger value="assignments" className="gap-1.5">
            <Users className="h-3.5 w-3.5" /> Assignments
          </TabsTrigger>
          <TabsTrigger value="audit" className="gap-1.5">
            <ScrollText className="h-3.5 w-3.5" /> Audit
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-3">
          <OverviewTab tool={tool} />
        </TabsContent>

        <TabsContent value="versions">
          <VersionsTab id={id} />
        </TabsContent>

        <TabsContent value="assignments">
          <AssignmentsTab tool={tool} />
        </TabsContent>

        <TabsContent value="audit">
          <AuditTab tool={tool} />
        </TabsContent>
      </Tabs>

      {/* Unpublish dialog */}
      <AlertDialog open={unpublishOpen} onOpenChange={setUnpublishOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unpublish tool</AlertDialogTitle>
            <AlertDialogDescription>
              Choose how to retire this tool. <strong>Deprecated</strong> keeps
              existing tenant assignments but blocks new ones.{' '}
              <strong>Retired</strong> revokes ALL existing assignments and
              tenants will lose access immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Next status</Label>
              <Select
                value={unpublishNext}
                onValueChange={(v) =>
                  setUnpublishNext(v as UnpublishNextStatus)
                }
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="deprecated">
                    Deprecated (preserve assignments)
                  </SelectItem>
                  <SelectItem value="retired">
                    Retired (revoke all assignments)
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="up-reason" className="text-xs">
                Reason (min 10 characters — recorded in audit chain)
              </Label>
              <Textarea
                id="up-reason"
                value={unpublishReason}
                onChange={(e) => setUnpublishReason(e.target.value)}
                placeholder="Replaced by tool 'fetch-asset-v2' which adds vibration baseline lookup."
                rows={3}
                className="text-sm"
                data-testid="unpublish-reason-input"
              />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setUnpublishReason('')}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleUnpublish}
              disabled={
                unpublishReason.trim().length < 10 || unpublish.isPending
              }
              className={
                unpublishNext === 'retired'
                  ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                  : ''
              }
              data-testid="unpublish-confirm"
            >
              {unpublish.isPending
                ? 'Saving…'
                : unpublishNext === 'retired'
                  ? 'Retire tool'
                  : 'Deprecate tool'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirm */}
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently delete tool</AlertDialogTitle>
            <AlertDialogDescription>
              This will hard-delete{' '}
              <code className="bg-muted px-1 rounded">{tool.slug}</code> and
              all its version history. This cannot be undone. Allowed only in
              status='draft' or 'retired'.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
            >
              Delete tool
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit drawer */}
      <EditDrawer
        tool={tool}
        open={editOpen}
        onClose={() => setEditOpen(false)}
      />
    </div>
  );
}

// ─── Overview ──────────────────────────────────────────────────────────────

function OverviewTab({ tool }: { tool: SysadminToolDetail }) {
  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Description</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          {tool.description}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Lifecycle</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-2 gap-3 text-xs">
            <div>
              <dt className="text-muted-foreground">Status</dt>
              <dd className="mt-0.5">
                <StatusBadge status={tool.status} />
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Category</dt>
              <dd className="mt-0.5">{tool.category}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Policy</dt>
              <dd className="mt-0.5 font-mono">{tool.policy}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Side effects</dt>
              <dd className="mt-0.5">
                {tool.sideEffects.length === 0 ? (
                  <span className="text-muted-foreground italic">none</span>
                ) : (
                  <div className="flex gap-1 flex-wrap">
                    {tool.sideEffects.map((s) => (
                      <Badge
                        key={s}
                        variant="outline"
                        className="text-[10px]"
                      >
                        {s}
                      </Badge>
                    ))}
                  </div>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Published at</dt>
              <dd className="mt-0.5 font-mono">
                {tool.publishedAt
                  ? new Date(tool.publishedAt).toLocaleString()
                  : '—'}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Unpublished at</dt>
              <dd className="mt-0.5 font-mono">
                {tool.unpublishedAt
                  ? new Date(tool.unpublishedAt).toLocaleString()
                  : '—'}
              </dd>
            </div>
            {tool.unpublishReason && (
              <div className="col-span-2">
                <dt className="text-muted-foreground">Unpublish reason</dt>
                <dd className="mt-0.5 italic">{tool.unpublishReason}</dd>
              </div>
            )}
            {tool.mcpServerId && (
              <div className="col-span-2">
                <dt className="text-muted-foreground">MCP server ID</dt>
                <dd className="mt-0.5 font-mono">{tool.mcpServerId}</dd>
              </div>
            )}
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Input schema</CardTitle>
          <CardDescription className="text-xs">
            JSON Schema describing tool parameters. Returned to tenants in
            redacted form.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <pre className="text-[11px] font-mono bg-muted/40 p-3 rounded overflow-x-auto">
            {JSON.stringify(tool.inputSchema, null, 2)}
          </pre>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Output schema</CardTitle>
        </CardHeader>
        <CardContent>
          <pre className="text-[11px] font-mono bg-muted/40 p-3 rounded overflow-x-auto">
            {JSON.stringify(tool.outputSchema, null, 2)}
          </pre>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">
            Source config <span className="text-amber-600">(sysadmin-only)</span>
          </CardTitle>
          <CardDescription className="text-xs">
            Executor wiring. Never returned to tenants per I-M6.7 redaction
            invariant.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <pre className="text-[11px] font-mono bg-muted/40 p-3 rounded overflow-x-auto">
            {JSON.stringify(tool.sourceConfig, null, 2)}
          </pre>
        </CardContent>
      </Card>
    </>
  );
}

// ─── Versions ──────────────────────────────────────────────────────────────

function VersionsTab({ id }: { id: string }) {
  const { data, isLoading, error } = useSysadminToolVersions(id);
  if (isLoading) return <div className="p-4 animate-pulse">Loading…</div>;
  if (error)
    return (
      <Card className="border-destructive/30">
        <CardContent className="py-6 text-sm text-destructive flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>Failed to load: {error.message}</div>
        </CardContent>
      </Card>
    );
  const items = data?.items ?? [];
  if (items.length === 0)
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          No version history yet. Editing the tool creates the first snapshot.
        </CardContent>
      </Card>
    );
  return (
    <Card>
      <CardContent className="px-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-24">Version</TableHead>
              <TableHead>Snapshot summary</TableHead>
              <TableHead className="w-44">Changed</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((v) => {
              const snap = v.snapshot as Record<string, unknown>;
              const display =
                typeof snap?.display_name === 'string'
                  ? (snap.display_name as string)
                  : 'unknown';
              const policy =
                typeof snap?.policy === 'string'
                  ? (snap.policy as string)
                  : '';
              return (
                <TableRow key={v.id}>
                  <TableCell className="font-mono text-xs">
                    v{v.version}
                  </TableCell>
                  <TableCell className="text-xs">
                    <div className="font-medium">{display}</div>
                    <div className="text-[11px] text-muted-foreground">
                      policy: {policy}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground tabular-nums">
                    {new Date(v.changedAt).toLocaleString()}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

// ─── Assignments ───────────────────────────────────────────────────────────

function AssignmentsTab({ tool }: { tool: SysadminToolDetail }) {
  const id = tool.id;
  const { data, isLoading, error } = useSysadminToolAssignments(id);
  const assign = useAssignSysadminTool();
  const revoke = useRevokeSysadminToolAssignment();

  const [assignOpen, setAssignOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<{
    id: string;
    organizationName: string;
  } | null>(null);

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    try {
      await revoke.mutateAsync({
        id: revokeTarget.id,
        systemToolId: id,
      });
      toast.success(`Revoked ${revokeTarget.organizationName}`);
    } catch (err) {
      toast.error('Revoke failed', {
        description: err instanceof Error ? err.message : undefined,
      });
    }
    setRevokeTarget(null);
  };

  const items = data?.items ?? [];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {tool.status === 'published' ? (
            <>
              {items.length} {items.length === 1 ? 'tenant' : 'tenants'}{' '}
              assigned
            </>
          ) : tool.status === 'deprecated' ? (
            <>
              <PauseCircle className="h-3.5 w-3.5 inline mr-1 text-amber-600" />
              Deprecated — existing assignments preserved, no new assignments
            </>
          ) : tool.status === 'retired' ? (
            <>
              <Archive className="h-3.5 w-3.5 inline mr-1 text-rose-600" />
              Retired — all assignments revoked
            </>
          ) : (
            <>Tool is in draft. Publish to enable assignments.</>
          )}
        </div>
        {tool.status === 'published' && (
          <Button
            size="sm"
            className="gap-1.5"
            onClick={() => setAssignOpen(true)}
            data-testid="assign-tenant-button"
          >
            <Plus className="h-3.5 w-3.5" />
            Assign to tenant
          </Button>
        )}
      </div>

      {isLoading ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground animate-pulse">
            Loading assignments…
          </CardContent>
        </Card>
      ) : error ? (
        <Card className="border-destructive/30">
          <CardContent className="py-6 text-sm text-destructive flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>Failed to load: {error.message}</div>
          </CardContent>
        </Card>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            No tenants assigned.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="px-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tenant</TableHead>
                  <TableHead className="w-32">Status</TableHead>
                  <TableHead className="w-44">Assigned</TableHead>
                  <TableHead className="w-24 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell className="text-sm font-medium">
                      {a.organizationName}
                    </TableCell>
                    <TableCell>
                      {a.isEnabled ? (
                        <Badge
                          variant="default"
                          className="bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/20 text-[10px] gap-1"
                        >
                          <ShieldCheck className="h-2.5 w-2.5" />
                          Enabled
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="text-[10px]">
                          Disabled
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground tabular-nums">
                      {new Date(a.assignedAt).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        title="Revoke assignment"
                        className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10"
                        onClick={() =>
                          setRevokeTarget({
                            id: a.id,
                            organizationName: a.organizationName,
                          })
                        }
                        data-testid={`revoke-${a.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <AssignDrawer
        toolId={id}
        toolSlug={tool.slug}
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        existingAssignmentOrgIds={new Set(items.map((i) => i.organizationId))}
      />

      <AlertDialog
        open={revokeTarget !== null}
        onOpenChange={(open) => !open && setRevokeTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke tenant assignment</AlertDialogTitle>
            <AlertDialogDescription>
              Remove access to{' '}
              <code className="bg-muted px-1 rounded">{tool.slug}</code> for{' '}
              <strong>{revokeTarget?.organizationName}</strong>? Their agents
              will lose access immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleRevoke}
              data-testid="revoke-confirm"
            >
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function AssignDrawer({
  toolId,
  toolSlug,
  open,
  onClose,
  existingAssignmentOrgIds,
}: {
  toolId: string;
  toolSlug: string;
  open: boolean;
  onClose: () => void;
  existingAssignmentOrgIds: Set<string>;
}) {
  const { data, isLoading, error } = useTenantsForAssignment();
  const assign = useAssignSysadminTool();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const tenants = useMemo(() => {
    const all = data?.items ?? [];
    const q = search.trim().toLowerCase();
    return all
      .filter((t) => !existingAssignmentOrgIds.has(t.id))
      .filter(
        (t) =>
          !q ||
          t.name.toLowerCase().includes(q) ||
          t.slug.toLowerCase().includes(q),
      );
  }, [data, search, existingAssignmentOrgIds]);

  const toggleOne = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const handleAssign = async () => {
    if (selected.size === 0) return;
    let ok = 0;
    let fail = 0;
    for (const orgId of selected) {
      try {
        await assign.mutateAsync({
          organizationId: orgId,
          systemToolId: toolId,
        });
        ok++;
      } catch {
        fail++;
      }
    }
    if (ok > 0) toast.success(`Assigned ${ok} tenant${ok === 1 ? '' : 's'}`);
    if (fail > 0)
      toast.error(`${fail} assignment${fail === 1 ? '' : 's'} failed`);
    setSelected(new Set());
    setSearch('');
    onClose();
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side="right"
        className="sm:max-w-lg overflow-y-auto"
        data-testid="assign-drawer"
      >
        <SheetHeader>
          <SheetTitle>Assign "{toolSlug}" to tenants</SheetTitle>
          <SheetDescription>
            Select one or more tenants. Already-assigned tenants are hidden.
          </SheetDescription>
        </SheetHeader>
        <div className="mt-4 space-y-3">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Search tenants…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-9 text-sm"
            />
          </div>

          {isLoading ? (
            <div className="text-sm text-muted-foreground animate-pulse py-8 text-center">
              Loading tenants…
            </div>
          ) : error ? (
            <div className="text-sm text-destructive flex gap-2 py-4">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              {error.message}
            </div>
          ) : tenants.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center">
              {search
                ? 'No tenants match.'
                : 'All active tenants already have this tool.'}
            </div>
          ) : (
            <div className="border rounded max-h-[60vh] overflow-y-auto">
              {tenants.map((t) => (
                <label
                  key={t.id}
                  className="flex items-center gap-3 p-2 hover:bg-muted/50 cursor-pointer border-b last:border-b-0"
                  data-testid={`tenant-option-${t.slug}`}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(t.id)}
                    onChange={() => toggleOne(t.id)}
                    className="h-4 w-4"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium">{t.name}</div>
                    <div className="text-[11px] text-muted-foreground font-mono">
                      {t.slug}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={onClose}>
              <X className="h-3.5 w-3.5 mr-1" />
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleAssign}
              disabled={selected.size === 0 || assign.isPending}
              data-testid="assign-submit"
            >
              {assign.isPending
                ? 'Assigning…'
                : `Assign ${selected.size || ''}`}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─── Audit ─────────────────────────────────────────────────────────────────

function AuditTab({ tool }: { tool: SysadminToolDetail }) {
  // The audit stream endpoint doesn't filter by resourceId today; we
  // pull category=system and filter client-side by resourceId. Limit
  // pulls to 100 most-recent rows per refresh — sufficient for a tool
  // detail Audit tab.
  const { data, isLoading, error } = useAuditStream({
    category: 'system',
    limit: 100,
  });

  const items = useMemo(
    () =>
      (data?.items ?? []).filter(
        (r) =>
          r.resourceId === tool.id ||
          // assignment-level events tied via resourceType + matching
          // payload — not filterable cheaply on the client without
          // payload, so we include resourceType match on
          // 'tenant_tool_assignment' too. Imperfect but useful.
          r.resourceType === 'tenant_tool_assignment',
      ),
    [data, tool.id],
  );

  if (isLoading)
    return (
      <Card>
        <CardContent className="py-10 text-center text-muted-foreground animate-pulse">
          Loading audit…
        </CardContent>
      </Card>
    );
  if (error)
    return (
      <Card className="border-destructive/30">
        <CardContent className="py-6 text-sm text-destructive flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>Failed to load: {error.message}</div>
        </CardContent>
      </Card>
    );

  if (items.length === 0)
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          No audit events for this tool yet.
        </CardContent>
      </Card>
    );

  return (
    <Card>
      <CardContent className="px-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-44">When</TableHead>
              <TableHead className="w-56">Action</TableHead>
              <TableHead>Resource</TableHead>
              <TableHead className="w-40">Actor</TableHead>
              <TableHead className="w-24">Outcome</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((row) => (
              <TableRow key={row.id}>
                <TableCell className="text-xs text-muted-foreground tabular-nums">
                  {new Date(row.createdAt).toLocaleString()}
                </TableCell>
                <TableCell className="text-xs font-mono">
                  {row.action}
                </TableCell>
                <TableCell className="text-xs font-mono">
                  {row.resourceType}
                </TableCell>
                <TableCell className="text-xs">
                  {row.userEmail ?? row.userId ?? '—'}
                </TableCell>
                <TableCell>
                  <Badge
                    variant={
                      row.outcome === 'failure' ? 'destructive' : 'secondary'
                    }
                    className="text-[10px]"
                  >
                    {row.outcome ?? '—'}
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

// ─── Edit drawer ───────────────────────────────────────────────────────────

function EditDrawer({
  tool,
  open,
  onClose,
}: {
  tool: SysadminToolDetail;
  open: boolean;
  onClose: () => void;
}) {
  const update = useUpdateSysadminTool();
  const [displayName, setDisplayName] = useState(tool.displayName);
  const [description, setDescription] = useState(tool.description);
  const [category, setCategory] = useState(tool.category);
  const [policy, setPolicy] = useState<ToolPolicy>(tool.policy as ToolPolicy);
  const [sourceType, setSourceType] = useState<ToolSourceType>(
    tool.sourceType as ToolSourceType,
  );
  const [mcpServerId, setMcpServerId] = useState(tool.mcpServerId ?? '');
  const [sideEffectsRaw, setSideEffectsRaw] = useState(
    JSON.stringify(tool.sideEffects),
  );
  const [inputSchemaRaw, setInputSchemaRaw] = useState(
    JSON.stringify(tool.inputSchema, null, 2),
  );
  const [outputSchemaRaw, setOutputSchemaRaw] = useState(
    JSON.stringify(tool.outputSchema, null, 2),
  );
  const [sourceConfigRaw, setSourceConfigRaw] = useState(
    JSON.stringify(tool.sourceConfig, null, 2),
  );

  const handleSave = async () => {
    // PR-C-MINIMAL — 4 inline try/catch blocks replaced with shared
    // parsers. Toast UX unchanged.
    const inputP = parseJsonTyped(inputSchemaRaw, 'Input schema');
    if (!inputP.ok) {
      toast.error('Input schema must be valid JSON');
      return;
    }
    const outputP = parseJsonTyped(outputSchemaRaw, 'Output schema');
    if (!outputP.ok) {
      toast.error('Output schema must be valid JSON');
      return;
    }
    const sourceCfgP = parseJsonTyped(sourceConfigRaw, 'Source config');
    if (!sourceCfgP.ok) {
      toast.error('Source config must be valid JSON');
      return;
    }
    const sideEffectsP = tryParseJsonArrayOfStrings(
      sideEffectsRaw,
      'Side effects',
    );
    if (!sideEffectsP.ok) {
      toast.error('Side effects must be a JSON array of strings');
      return;
    }
    try {
      await update.mutateAsync({
        id: tool.id,
        displayName,
        description,
        category,
        policy,
        sourceType,
        mcpServerId: mcpServerId.trim() || null,
        sideEffects: sideEffectsP.value,
        inputSchema: inputP.value as Record<string, unknown>,
        outputSchema: outputP.value as Record<string, unknown>,
        sourceConfig: sourceCfgP.value as Record<string, unknown>,
      });
      toast.success(`Updated "${tool.slug}" — new version created`);
      onClose();
    } catch (err) {
      toast.error('Save failed', {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side="right"
        className="sm:max-w-2xl overflow-y-auto"
        data-testid="edit-drawer"
      >
        <SheetHeader>
          <SheetTitle>Edit tool — {tool.slug}</SheetTitle>
          <SheetDescription>
            Slug is immutable. Saving creates a new version snapshot.
          </SheetDescription>
        </SheetHeader>
        <div className="mt-4 space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Display name</Label>
            <Input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="h-8 text-sm"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Description</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="text-sm"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Category</Label>
              <Input
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="h-8 text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Policy</Label>
              <Select
                value={policy}
                onValueChange={(v) => setPolicy(v as ToolPolicy)}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto_approve">Auto-approve</SelectItem>
                  <SelectItem value="require_approval">
                    Require approval
                  </SelectItem>
                  <SelectItem value="forbidden">Forbidden</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Source type</Label>
              <Select
                value={sourceType}
                onValueChange={(v) => setSourceType(v as ToolSourceType)}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="builtin">Built-in</SelectItem>
                  <SelectItem value="mcp">MCP</SelectItem>
                  <SelectItem value="openapi">OpenAPI</SelectItem>
                  <SelectItem value="http">HTTP</SelectItem>
                  <SelectItem value="database">Database</SelectItem>
                  <SelectItem value="custom_function">
                    Custom function
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Side effects (JSON array)</Label>
              <Input
                value={sideEffectsRaw}
                onChange={(e) => setSideEffectsRaw(e.target.value)}
                className="font-mono text-xs h-8"
              />
            </div>
          </div>
          {sourceType === 'mcp' && (
            <div className="space-y-1.5">
              <Label className="text-xs">MCP server ID</Label>
              <Input
                value={mcpServerId}
                onChange={(e) => setMcpServerId(e.target.value)}
                className="font-mono text-xs h-8"
                placeholder="(populated from MCP catalog)"
              />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Input schema</Label>
              <Textarea
                value={inputSchemaRaw}
                onChange={(e) => setInputSchemaRaw(e.target.value)}
                rows={6}
                className="font-mono text-xs"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Output schema</Label>
              <Textarea
                value={outputSchemaRaw}
                onChange={(e) => setOutputSchemaRaw(e.target.value)}
                rows={6}
                className="font-mono text-xs"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Source config</Label>
            <Textarea
              value={sourceConfigRaw}
              onChange={(e) => setSourceConfigRaw(e.target.value)}
              rows={4}
              className="font-mono text-xs"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={onClose}>
              <X className="h-3.5 w-3.5 mr-1" /> Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={update.isPending}
              data-testid="edit-submit"
            >
              <Save className="h-3.5 w-3.5 mr-1" />
              {update.isPending ? 'Saving…' : 'Save (new version)'}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
